import { addDays, format } from "date-fns";
import { ChevronLeft, ChevronRight, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { PAYMENT_LABEL, PAYMENT_BADGE, TS_LABEL, TS_BADGE, RINK_LABEL, euro } from "@/lib/rinks";

export default function DayView({ date, onDateChange, bookings, rink, onBookingClick }) {
  const shift = (n) => onDateChange(format(addDays(new Date(`${date}T00:00:00`), n), "yyyy-MM-dd"));

  const printDay = async () => {
    try {
      const res = await api.get("/bookings/daily/pdf", { params: { rink, date }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const totals = bookings.reduce(
    (a, b) => ({ skaters: a.skaters + b.number_attending, revenue: a.revenue + (b.total || 0) }),
    { skaters: 0, revenue: 0 }
  );

  return (
    <div className="card-brutal shadow-[6px_6px_0px_0px_#0f172a]" data-testid="daily-list">
      <div className="flex items-center justify-between flex-wrap gap-3 bg-slate-900 text-white p-3 border-b-2 border-slate-900">
        <div className="flex items-center gap-3">
          <button className="btn-secondary !px-2.5 !py-1.5" onClick={() => shift(-1)} data-testid="day-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-sky-300">{RINK_LABEL[rink]}</div>
            <div className="font-display text-lg md:text-xl uppercase leading-tight" data-testid="day-title">
              {format(new Date(`${date}T00:00:00`), "EEEE d MMMM yyyy")}
            </div>
          </div>
          <button className="btn-secondary !px-2.5 !py-1.5" onClick={() => shift(1)} data-testid="day-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            className="input-brutal !w-auto !py-1.5"
            value={date}
            onChange={(e) => e.target.value && onDateChange(e.target.value)}
            data-testid="day-date-input"
          />
          <button className="btn-primary !py-2" onClick={printDay} data-testid="day-print-pdf">
            <Printer className="w-4 h-4" /> Print day PDF
          </button>
        </div>
      </div>

      {bookings.length === 0 ? (
        <div className="p-10 text-center font-mono-tight text-xs uppercase tracking-widest text-slate-500" data-testid="daily-list-empty">
          No bookings on this day — use the arrows to move through the list
        </div>
      ) : (
        <div className="divide-y-2 divide-slate-900" data-testid="daily-list-rows">
          {bookings.map((b) => (
            <button
              key={b.id}
              onClick={() => onBookingClick(b)}
              className="w-full text-left p-3.5 hover:bg-sky-50 transition-colors flex items-center gap-4 flex-wrap"
              data-testid={`day-booking-${b.id}`}
            >
              <span className="badge-brutal bg-slate-900 text-white w-[72px] text-center">{b.session_time}</span>
              <span className="font-bold text-sm min-w-[160px] flex-1">{b.group_name}
                <span className="block text-xs font-normal text-slate-500">{b.contact_name}</span>
              </span>
              <span className="font-mono-tight text-xs font-bold uppercase tracking-wider text-slate-600">
                {b.number_attending} skaters
              </span>
              {(b.skating_aids > 0 || b.free_aids > 0) && (
                <span className="font-mono-tight text-xs uppercase tracking-wider text-slate-600">
                  {b.skating_aids > 0 ? `${b.skating_aids} aids` : ""}{b.skating_aids > 0 && b.free_aids > 0 ? " · " : ""}{b.free_aids > 0 ? `${b.free_aids} free` : ""}
                </span>
              )}
              {b.party_room && (
                <span className="badge-brutal bg-indigo-400 text-white !text-[10px]">
                  Party {b.party_room_from}–{b.party_room_to}
                </span>
              )}
              <span className={`badge-brutal ${PAYMENT_BADGE[b.payment_status || "unpaid"]} !text-[10px] !px-1.5`}>
                {PAYMENT_LABEL[b.payment_status || "unpaid"]}
              </span>
              {b.allocation_removed && TS_LABEL[b.allocation_removed] && (
                <span className={`badge-brutal ${TS_BADGE[b.allocation_removed]} !text-[10px] !px-1.5`} title="Ticketsolve allocation status">{TS_LABEL[b.allocation_removed]}</span>
              )}
              <span className="font-mono-tight text-sm font-bold">{euro(b.total)}</span>
            </button>
          ))}
        </div>
      )}

      <div className="bg-amber-300 border-t-2 border-slate-900 p-3 flex items-center justify-between flex-wrap gap-2" data-testid="daily-list-totals">
        <span className="font-mono-tight text-xs uppercase tracking-widest font-bold">
          {bookings.length} {bookings.length === 1 ? "booking" : "bookings"} · {totals.skaters} skaters
        </span>
        <span className="font-display text-lg">Total value {euro(totals.revenue)}</span>
      </div>
    </div>
  );
}
